import { theme as antTheme, type ThemeConfig } from "antd";

/**
 * Design system (ADR-0055). One place for every colour, radius, shadow, spacing and type value.
 *
 * - Cool neutral greys, ONE accent (indigo), semantic colours that also differ in lightness.
 * - Light and dark palettes; every text/background pair used by the console is ≥ 4.5:1
 *   (checked by `kit.test.tsx` with `contrastRatio`).
 * - Applied twice: as antd `ConfigProvider` tokens (so every antd screen follows) and as CSS custom
 *   properties `--ap-*` on `<html>` (for the shell's own CSS and inline styles).
 *
 * Fonts are bundled via @fontsource (SIL OFL 1.1) — never loaded from a CDN (air gap). IBM Plex Sans
 * Arabic ships the IBM Plex Sans Latin glyphs, so one family covers both scripts.
 */
export const FONT_STACK = "'IBM Plex Sans Arabic', 'IBM Plex Sans', 'Segoe UI', Tahoma, system-ui, sans-serif";

export type ColorMode = "light" | "dark";

/** 4/8 px spacing scale. */
export const space = { 1: 4, 2: 8, 3: 12, 4: 16, 5: 24, 6: 32, 7: 48 } as const;
export const radius = { sm: 8, md: 10, lg: 12, pill: 999 } as const;
/** Type scale (px). */
export const typeScale = { xs: 12, sm: 13, md: 14, lg: 16, xl: 20, xxl: 24, display: 30 } as const;
/** Transitions stay ≤ 200 ms and are disabled under prefers-reduced-motion. */
export const motionMs = 160;

export interface Palette {
  accent: string;
  accentHover: string;
  /** Text/icon colour on an `accent` background. */
  onAccent: string;
  accentSoft: string;
  accentSoftText: string;
  bgLayout: string;
  surface: string;
  surfaceSunken: string;
  surfaceElevated: string;
  border: string;
  borderStrong: string;
  text: string;
  textSecondary: string;
  textTertiary: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  focus: string;
  shadowSm: string;
  shadowMd: string;
  shadowLg: string;
  overlay: string;
}

export const palettes: Record<ColorMode, Palette> = {
  light: {
    accent: "#3b5bdb",
    accentHover: "#364fc7",
    onAccent: "#ffffff",
    accentSoft: "#edf2ff",
    accentSoftText: "#2f44ad",
    bgLayout: "#f4f6f9",
    surface: "#ffffff",
    surfaceSunken: "#eef1f5",
    surfaceElevated: "#ffffff",
    border: "#e2e6ec",
    borderStrong: "#c9d0da",
    text: "#161b26",
    textSecondary: "#485165",
    textTertiary: "#586174",
    success: "#2b7a3b",
    warning: "#8f5a00",
    danger: "#c92a2a",
    info: "#1864ab",
    focus: "#3b5bdb",
    shadowSm: "0 1px 2px rgba(16, 24, 40, 0.06)",
    shadowMd: "0 1px 2px rgba(16, 24, 40, 0.05), 0 4px 12px rgba(16, 24, 40, 0.06)",
    shadowLg: "0 4px 8px rgba(16, 24, 40, 0.06), 0 16px 32px rgba(16, 24, 40, 0.10)",
    overlay: "rgba(15, 20, 30, 0.45)",
  },
  dark: {
    accent: "#8ea2f8",
    accentHover: "#a5b4fc",
    onAccent: "#0e1117",
    accentSoft: "#232c4a",
    accentSoftText: "#c5d0ff",
    bgLayout: "#0e1117",
    surface: "#151922",
    surfaceSunken: "#1b2029",
    surfaceElevated: "#1e2430",
    border: "#2a313d",
    borderStrong: "#3a4352",
    text: "#e7eaf0",
    textSecondary: "#b4bcc9",
    textTertiary: "#9ca5b4",
    success: "#69db7c",
    warning: "#ffc94d",
    danger: "#ff8787",
    info: "#74c0fc",
    focus: "#a5b4fc",
    shadowSm: "0 1px 2px rgba(0, 0, 0, 0.4)",
    shadowMd: "0 1px 2px rgba(0, 0, 0, 0.35), 0 4px 12px rgba(0, 0, 0, 0.35)",
    shadowLg: "0 4px 8px rgba(0, 0, 0, 0.35), 0 16px 32px rgba(0, 0, 0, 0.5)",
    overlay: "rgba(0, 0, 0, 0.6)",
  },
};

export type Tone = "success" | "warning" | "error" | "info" | "neutral";
interface TonePair {
  color: string;
  background: string;
  border: string;
}

/** Accessible foreground/background pairs for status tags (contrast ≥ 4.5:1), per mode. */
export const statusPalettes: Record<ColorMode, Record<Tone, TonePair>> = {
  light: {
    success: { color: "#0f5323", background: "#e6f4ea", border: "#a7d8b4" },
    warning: { color: "#6b4600", background: "#fff4d6", border: "#f2cf7a" },
    error: { color: "#8e1b1b", background: "#fdecec", border: "#f2b0b0" },
    info: { color: "#1f3a9e", background: "#edf2ff", border: "#bac8ff" },
    neutral: { color: "#2f3742", background: "#eef1f5", border: "#c9d0da" },
  },
  dark: {
    success: { color: "#8ce99a", background: "#12301b", border: "#2b6a3a" },
    warning: { color: "#ffd8a8", background: "#3a2a0c", border: "#7a5a1a" },
    error: { color: "#ffa8a8", background: "#3b1618", border: "#7a2e33" },
    info: { color: "#c5d0ff", background: "#1d2545", border: "#3f4f99" },
    neutral: { color: "#ced4da", background: "#232933", border: "#3a4352" },
  },
};

const TONES: Tone[] = ["success", "warning", "error", "info", "neutral"];

/**
 * Status colours as CSS variables (with the light value as fallback), so StatusTag and SlaTimer
 * follow the colour mode without re-rendering.
 */
export const statusPalette = Object.fromEntries(
  TONES.map((tone) => {
    const l = statusPalettes.light[tone];
    return [
      tone,
      {
        color: `var(--ap-status-${tone}-fg, ${l.color})`,
        background: `var(--ap-status-${tone}-bg, ${l.background})`,
        border: `var(--ap-status-${tone}-border, ${l.border})`,
      },
    ];
  }),
) as Record<Tone, TonePair>;

/** Back-compat: the light tokens as plain values. */
export const tokens = {
  colorPrimary: palettes.light.accent,
  colorSuccess: palettes.light.success,
  colorWarning: palettes.light.warning,
  colorError: palettes.light.danger,
  colorInfo: palettes.light.info,
  colorText: palettes.light.text,
  colorTextSecondary: palettes.light.textSecondary,
  colorBgLayout: palettes.light.bgLayout,
  borderRadius: radius.md,
  fontFamily: FONT_STACK,
  fontSize: typeScale.md,
} as const;

/** antd theme for a colour mode. `reducedMotion` turns antd's own motion off. */
export function buildTheme(mode: ColorMode, reducedMotion = false): ThemeConfig {
  const p = palettes[mode];
  const s = statusPalettes[mode];
  return {
    algorithm: mode === "dark" ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
    cssVar: false,
    token: {
      colorPrimary: p.accent,
      // Hover/active states stay ≥ 4.5:1 as text (antd would derive a lighter hover blue).
      colorPrimaryHover: p.accentHover,
      colorPrimaryActive: p.accentSoftText,
      colorPrimaryTextHover: p.accentHover,
      colorPrimaryTextActive: p.accentSoftText,
      colorLink: p.accent,
      colorLinkHover: p.accentHover,
      colorSuccess: p.success,
      colorWarning: p.warning,
      colorError: p.danger,
      colorInfo: p.info,
      colorTextLightSolid: p.onAccent,
      colorText: p.text,
      colorTextHeading: p.text,
      colorTextSecondary: p.textSecondary,
      colorTextTertiary: p.textTertiary,
      colorTextDescription: p.textSecondary,
      colorTextPlaceholder: p.textTertiary,
      colorTextLabel: p.textSecondary,
      colorIcon: p.textSecondary,
      colorBgLayout: p.bgLayout,
      colorBgContainer: p.surface,
      colorBgElevated: p.surfaceElevated,
      colorBorder: p.borderStrong,
      colorBorderSecondary: p.border,
      colorSplit: p.border,
      colorFillAlter: p.surfaceSunken,
      colorBgSpotlight: mode === "dark" ? p.text : "#1f2633",
      colorSuccessBg: s.success.background,
      colorSuccessBorder: s.success.border,
      colorWarningBg: s.warning.background,
      colorWarningBorder: s.warning.border,
      colorErrorBg: s.error.background,
      colorErrorBorder: s.error.border,
      colorInfoBg: s.info.background,
      colorInfoBorder: s.info.border,
      borderRadius: radius.md,
      borderRadiusLG: radius.lg,
      borderRadiusSM: radius.sm,
      borderRadiusXS: 4,
      boxShadow: p.shadowMd,
      boxShadowSecondary: p.shadowLg,
      boxShadowTertiary: p.shadowSm,
      fontFamily: FONT_STACK,
      fontSize: typeScale.md,
      fontSizeHeading1: 28,
      fontSizeHeading2: 22,
      fontSizeHeading3: 18,
      lineHeight: 1.6,
      controlHeight: 36,
      motion: !reducedMotion,
      motionDurationMid: "0.16s",
      motionDurationSlow: "0.2s",
    },
    components: {
      Layout: { headerBg: p.surface, siderBg: p.surface, bodyBg: p.bgLayout, headerPadding: "0 24px" },
      Card: { headerFontSize: typeScale.lg, paddingLG: 20 },
      Table: { headerBg: p.surfaceSunken, headerColor: p.textSecondary, rowHoverBg: p.accentSoft, cellPaddingBlock: 12 },
      Tabs: { itemColor: p.textSecondary, itemSelectedColor: p.accentSoftText, inkBarColor: p.accent, titleFontSize: typeScale.md },
      Menu: { itemBorderRadius: radius.sm, itemSelectedBg: p.accentSoft, itemSelectedColor: p.accentSoftText },
      Button: { fontWeight: 500, primaryShadow: "none", defaultShadow: "none", dangerShadow: "none" },
      Tag: { defaultColor: p.text, defaultBg: p.surfaceSunken },
      Tooltip: { colorTextLightSolid: mode === "dark" ? p.bgLayout : "#ffffff" },
      Modal: { titleFontSize: typeScale.lg },
      Drawer: { footerPaddingBlock: 12 },
      Segmented: { itemSelectedColor: p.text },
      Badge: { textFontWeight: 600 },
    },
  };
}

/** The light theme (default; kept for callers that import `theme`). */
export const theme: ThemeConfig = buildTheme("light");

/** CSS custom properties for a mode (applied on `<html>` by AppProviders). */
export function cssVars(mode: ColorMode): Record<string, string> {
  const p = palettes[mode];
  const vars: Record<string, string> = {
    "--ap-accent": p.accent,
    "--ap-accent-hover": p.accentHover,
    "--ap-on-accent": p.onAccent,
    "--ap-accent-soft": p.accentSoft,
    "--ap-accent-soft-text": p.accentSoftText,
    "--ap-bg": p.bgLayout,
    "--ap-surface": p.surface,
    "--ap-surface-sunken": p.surfaceSunken,
    "--ap-surface-elevated": p.surfaceElevated,
    "--ap-border": p.border,
    "--ap-border-strong": p.borderStrong,
    "--ap-text": p.text,
    "--ap-text-secondary": p.textSecondary,
    "--ap-text-tertiary": p.textTertiary,
    "--ap-success": p.success,
    "--ap-warning": p.warning,
    "--ap-danger": p.danger,
    "--ap-info": p.info,
    "--ap-focus": p.focus,
    "--ap-shadow-sm": p.shadowSm,
    "--ap-shadow-md": p.shadowMd,
    "--ap-shadow-lg": p.shadowLg,
    "--ap-overlay": p.overlay,
    "--ap-radius-sm": `${radius.sm}px`,
    "--ap-radius-md": `${radius.md}px`,
    "--ap-radius-lg": `${radius.lg}px`,
    "--ap-font": FONT_STACK,
    "--ap-motion": `${motionMs}ms`,
  };
  for (const [k, v] of Object.entries(space)) vars[`--ap-space-${k}`] = `${v}px`;
  for (const tone of TONES) {
    const pair = statusPalettes[mode][tone];
    vars[`--ap-status-${tone}-fg`] = pair.color;
    vars[`--ap-status-${tone}-bg`] = pair.background;
    vars[`--ap-status-${tone}-border`] = pair.border;
  }
  return vars;
}

/** WCAG 2.x relative luminance of `#rrggbb`. */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) throw new Error(`not a #rrggbb colour: ${hex}`);
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => {
    const c = parseInt(h as string, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two `#rrggbb` colours (1–21). */
export function contrastRatio(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p) as [number, number];
  return (x + 0.05) / (y + 0.05);
}
